<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">My Performance Appraisals</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Employee Self Service</a></li>
                    <li class="breadcrumb-item active">My Appraisals</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Statistics Cards -->
          <div class="row mb-4">
            <div class="col-xl-3 col-md-6">
              <div class="card card-animate">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <p class="text-muted mb-2">Total Appraisals</p>
                      <h3 class="mb-0">{{ statistics.totalAppraisals }}</h3>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="avatar-sm rounded-circle bg-primary mini-stat-icon">
                        <span class="avatar-title rounded-circle bg-primary">
                          <i class="bx bx-file font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-animate">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <p class="text-muted mb-2">Average Rating</p>
                      <h3 class="mb-0">{{ statistics.averageScore }}<small class="text-muted">/5</small></h3>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="avatar-sm rounded-circle bg-success mini-stat-icon">
                        <span class="avatar-title rounded-circle bg-success">
                          <i class="bx bx-star font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-animate">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <p class="text-muted mb-2">Completed</p>
                      <h3 class="mb-0">{{ statistics.completedCount }}</h3>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="avatar-sm rounded-circle bg-info mini-stat-icon">
                        <span class="avatar-title rounded-circle bg-info">
                          <i class="bx bx-check-circle font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-animate">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <p class="text-muted mb-2">Pending</p>
                      <h3 class="mb-0">{{ statistics.pendingCount }}</h3>
                    </div>
                    <div class="flex-shrink-0">
                      <div class="avatar-sm rounded-circle bg-warning mini-stat-icon">
                        <span class="avatar-title rounded-circle bg-warning">
                          <i class="bx bx-time-five font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters & Actions -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <div class="row g-3">
                        <div class="col-md-4">
                          <label class="form-label">Year</label>
                          <select v-model="filters.year" @change="loadAppraisals" class="form-select">
                            <option value="">All Years</option>
                            <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                          </select>
                        </div>
                        <div class="col-md-4">
                          <label class="form-label">Status</label>
                          <select v-model="filters.status" @change="loadAppraisals" class="form-select">
                            <option value="">All Status</option>
                            <option value="Draft">Draft</option>
                            <option value="Submitted">Submitted</option>
                            <option value="ReviewedByManager">Reviewed By Manager</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                          </select>
                        </div>
                        <div class="col-md-4">
                          <label class="form-label">&nbsp;</label>
                          <button class="btn btn-outline-secondary w-100" @click="resetFilters">
                            <i class="mdi mdi-refresh me-1"></i> Reset
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 text-end">
                      <label class="form-label">&nbsp;</label>
                      <button class="btn btn-primary w-100" @click="startNewAppraisal">
                        <i class="mdi mdi-plus me-1"></i> Start New Appraisal
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Appraisals List -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-2">Loading appraisals...</p>
                  </div>

                  <div v-else-if="appraisals.length === 0" class="text-center py-5">
                    <i class="mdi mdi-file-document-outline display-4 text-muted"></i>
                    <h5 class="mt-3">No Appraisals Found</h5>
                    <p class="text-muted">You haven't created any appraisals yet.</p>
                    <button class="btn btn-primary mt-3" @click="startNewAppraisal">
                      <i class="mdi mdi-plus me-1"></i> Start Your First Appraisal
                    </button>
                  </div>

                  <div v-else class="table-responsive">
                    <table class="table table-hover align-middle mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Appraisal Period</th>
                        <th>Year</th>
                        <th>Status</th>
                        <th>Overall Rating</th>
                        <th>Appraiser</th>
                        <th>Reviewer</th>
                        <th>Created Date</th>
                        <th>Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="appraisal in appraisals" :key="appraisal.id">
                        <td>
                          <div class="fw-medium">{{ appraisal.periodStart }} - {{ appraisal.periodEnd }}</div>
                        </td>
                        <td>
                          <span class="badge bg-primary-subtle text-primary">{{ appraisal.appraisalYear }}</span>
                        </td>
                        <td>
                            <span :class="getStatusBadgeClass(appraisal.status)">
                              {{ appraisal.status }}
                            </span>
                        </td>
                        <td>
                          <div v-if="appraisal.finalOverallRating">
                            <div class="d-flex align-items-center">
                              <div class="flex-shrink-0 me-2">
                                <i class="mdi mdi-star text-warning"></i>
                              </div>
                              <div class="flex-grow-1">
                                <strong>{{ parseFloat(appraisal.finalOverallRating).toFixed(2) }}</strong>/5
                                <div class="progress mt-1" style="height: 5px;">
                                  <div class="progress-bar bg-warning"
                                       :style="{ width: (appraisal.finalOverallRating / 5 * 100) + '%' }">
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <span v-else class="text-muted">Not Rated Yet</span>
                        </td>
                        <td>
                          <span v-if="appraisal.appraiserName">{{ appraisal.appraiserName }}</span>
                          <span v-else class="text-muted">Not Assigned</span>
                        </td>
                        <td>
                          <span v-if="appraisal.reviewerName">{{ appraisal.reviewerName }}</span>
                          <span v-else class="text-muted">Not Assigned</span>
                        </td>
                        <td>{{ appraisal.createdDate }}</td>
                        <td>
                          <div class="dropdown">
                            <button class="btn btn-sm btn-light dropdown-toggle" type="button"
                                    data-bs-toggle="dropdown">
                              <i class="mdi mdi-dots-horizontal"></i>
                            </button>
                            <ul class="dropdown-menu">
                              <li>
                                <a class="dropdown-item" href="javascript:void(0)"
                                   @click="viewAppraisal(appraisal)">
                                  <i class="mdi mdi-eye me-2"></i> View Details
                                </a>
                              </li>
                              <li v-if="appraisal.status === 'Draft'">
                                <a class="dropdown-item" href="javascript:void(0)"
                                   @click="editAppraisal(appraisal)">
                                  <i class="mdi mdi-pencil me-2"></i> Continue Editing
                                </a>
                              </li>
                              <li v-if="appraisal.status === 'Approved'">
                                <a class="dropdown-item" href="javascript:void(0)"
                                   @click="downloadPDF(appraisal)">
                                  <i class="mdi mdi-download me-2"></i> Download PDF
                                </a>
                              </li>
                              <li v-if="canDelete(appraisal)">
                                <hr class="dropdown-divider">
                                <a class="dropdown-item text-danger" href="javascript:void(0)"
                                   @click="confirmDelete(appraisal)">
                                  <i class="mdi mdi-delete me-2"></i> Delete
                                </a>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
        <!-- end container-fluid -->
      </div>
      <!-- End Page-content -->
    </div>
    <!-- end content-minimize -->
  </div>
  <!-- end layout-wrapper -->
</template>

<script>
import { authUrl, execute } from "@/api";
import NavigationBar from "@/components/Navigation";

export default {
  name: "MyAppraisals",

  components: {
    NavigationBar
  },

  data() {
    return {
      loading: false,
      appraisals: [],
      years: [],
      filters: {
        year: '',
        status: ''
      },
      statistics: {
        totalAppraisals: 0,
        averageScore: 0,
        completedCount: 0,
        pendingCount: 0
      }
    };
  },

  mounted() {
    this.loadAppraisals();
    this.generateYears();
  },

  methods: {
    async loadAppraisals() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getMyAppraisals");

        if (this.filters.year) {
          data.append("year", this.filters.year);
        }
        if (this.filters.status) {
          data.append("status", this.filters.status);
        }

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.appraisals = res.data.data || [];
          this.calculateStatistics();
        } else {
          this.appraisals = [];
        }
      } catch (error) {
        console.error("Failed to load appraisals:", error);
        this.$toast?.error("Failed to load appraisals") || alert("Failed to load appraisals");
      } finally {
        this.loading = false;
      }
    },

    calculateStatistics() {
      const all = this.appraisals;

      this.statistics.totalAppraisals = all.length;

      const rated = all.filter(a => a.finalOverallRating);
      this.statistics.averageScore = rated.length > 0
          ? (rated.reduce((sum, a) => sum + parseFloat(a.finalOverallRating), 0) / rated.length).toFixed(2)
          : 0;

      this.statistics.completedCount = all.filter(a => a.status === 'Approved').length;
      this.statistics.pendingCount = all.filter(a =>
          a.status === 'Draft' || a.status === 'Submitted' || a.status === 'ReviewedByManager'
      ).length;
    },

    generateYears() {
      const currentYear = new Date().getFullYear();
      this.years = [];
      for (let i = 0; i < 5; i++) {
        this.years.push(currentYear - i);
      }
    },

    resetFilters() {
      this.filters.year = '';
      this.filters.status = '';
      this.loadAppraisals();
    },

    startNewAppraisal() {
      this.$router.push('/employee-self-assessment');
    },

    viewAppraisal(appraisal) {
      this.$router.push({
        path: '/appraisal-details',
        query: { id: appraisal.id }
      });
    },

    editAppraisal(appraisal) {
      this.$router.push({
        path: '/employee-self-assessment',
        query: { id: appraisal.id }
      });
    },

    downloadPDF(appraisal) {
      window.open(`${authUrl}?function=downloadAppraisalPDF&appraisalId=${appraisal.id}`, '_blank');
    },

    canDelete(appraisal) {
      return appraisal.status === 'Draft';
    },

    confirmDelete(appraisal) {
      if (confirm('Are you sure you want to delete this appraisal? This action cannot be undone.')) {
        this.deleteAppraisal(appraisal.id);
      }
    },

    async deleteAppraisal(appraisalId) {
      try {
        const data = new FormData();
        data.append("function", "deleteAppraisal");
        data.append("appraisalId", appraisalId);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Appraisal deleted successfully") || alert("Appraisal deleted successfully");
          this.loadAppraisals();
        } else {
          throw new Error(res?.data?.message || "Failed to delete appraisal");
        }
      } catch (error) {
        console.error("Failed to delete appraisal:", error);
        this.$toast?.error("Failed to delete appraisal") || alert("Failed to delete appraisal");
      }
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Draft': 'badge bg-secondary',
        'Submitted': 'badge bg-info',
        'ReviewedByManager': 'badge bg-primary',
        'Approved': 'badge bg-success',
        'Rejected': 'badge bg-danger'
      };
      return classes[status] || 'badge bg-secondary';
    }
  }
};
</script>

<style scoped>
.card-animate {
  transition: all 0.3s ease;
}

.card-animate:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.avatar-sm {
  height: 3rem;
  width: 3rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
  color: white;
}

.mini-stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.progress {
  border-radius: 10px;
}

.dropdown-menu {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}
</style>